import React from "react";
import { type NextPage } from "next";
import { Page } from "components/page";
import { getLandingPageProps, getPageMetaImage, getSafeCustomer, type LandingPageProps } from "utils";
import {
  CustomerStats,
  CustomerWithStatsSlug,
  DestinationMarquee,
  IndustryLeaderAwards,
  TraditionalCdpVsHightouch,
} from "components/content";
import { StackedLogos } from "components/logos";
import { Section, sectionSpacingY } from "components/section";
import { TextTitle } from "ui/text";
import { Button } from "ui/button";
import { AiDecisioningSizzleReel, UseCaseTabs } from "components/platform-overview-content";
import { AuraPlate } from "ui/plates";
import { CenteredQuote } from "components/quotes";
import { BookDemoButton } from "components/event-tracking";
import { CaseStudyExamples, MarketingHero, ProductFeatureSlideshow } from "components/solutions/marketing";

const customersForStats = [
  CustomerWithStatsSlug.PETSMART.toString(),
  CustomerWithStatsSlug.WMG.toString(),
  CustomerWithStatsSlug.WEIGHT_WATCHERS.toString(),
];
const otherFeaturedCustomers = ["chime", "minno"];

const Marketing: NextPage<LandingPageProps> = ({
  page,
  announcementBanner,
  defaultLogos,
  defaultMobileLogos,
  customers,
  destinations,
}) => {
  const petsmart = getSafeCustomer(customers, "petsmart");
  const chime = getSafeCustomer(customers, "chime");
  const minno = getSafeCustomer(customers, "minno");

  return (
    <Page
      title={page.metaTitle}
      description={page.metaDescription}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      announcementBanner={announcementBanner}
      bgColor="new.yellow.150"
      footerOptions={{ variant: "yellow" }}
    >
      <MarketingHero eyebrow={page.eyebrow} title={page.title} description={page.description} />

      <StackedLogos
        customers={defaultLogos}
        mobileCustomers={defaultMobileLogos}
        my="0"
        pt="small"
        pb={sectionSpacingY}
      />

      <CustomerStats
        customers={customersForStats.map((slug) => getSafeCustomer(customers, slug))}
        my="0"
        mb={sectionSpacingY}
      />

      <Section mb="0">
        <TextTitle
          size="lg"
          maxW="670px"
          align="center"
          title="A complete data platform built for marketers"
          description="Get any data to all of your tools to power real-time personalization, omni-channel journeys, and higher performing campaigns."
          actions={
            <Button hasRightChevron href="/platform/customer-studio">
              Explore Customer Studio
            </Button>
          }
        />

        <ProductFeatureSlideshow />
      </Section>

      <TraditionalCdpVsHightouch
        titleMaxW="750px"
        title="Marketers, you deserve a CDP that actually delivers"
        description="Don’t put yourself through a long, painful implementation with a traditional CDP just to fail months later. Get started with your existing data today."
        actions={<BookDemoButton text="Talk to an expert" />}
      />

      <Section>
        <TextTitle
          size="lg"
          align="center"
          title="Customers drive more impact, faster with Hightouch"
          description="Personalize and optimize any campaign in minutes. No waiting on engineering tickets or finding data discrepancies."
          descriptionMaxW="600px"
          mb="huge"
        />

        <CaseStudyExamples
          examples={[
            {
              customer: petsmart,
              chipText: "Accelerate growth",
              title: "Power your marketing at scale",
              description:
                "Unlock 1-1 personalization on every channel at any scale. Use all of your customer data to curate the perfect user experience for each customer.",
            },
            {
              customer: chime,
              chipText: "Get results fast",
              title: "See value immediately",
              description:
                "Skip the 6-9 month traditional CDP implementation and have a live use case before you sign a contract. Hightouch leverages your existing data infrastructure so you can get started immediately.",
            },
            {
              customer: minno,
              chipText: "Move faster",
              title: "Get world-class support",
              description:
                "Don’t do it alone. Product experts will be with you every step of the way for when even the smallest question comes up. Work with us and see why we have a 99%+ customer satisfaction score.",
            },
          ]}
        />
      </Section>

      <AuraPlate condensed variant="yellow" mt="biggest">
        <CenteredQuote
          quoteText="We’re activating millions of records every day to personalize journeys for the 65+ million members in our loyalty program."
          author={{ name: "Bradley Breuer", job: "VP Marketing" }}
          company={{
            name: "PetSmart",
            logo: petsmart.logoUrl,
          }}
          maxW="4xl"
        />
      </AuraPlate>

      <AiDecisioningSizzleReel mt={sectionSpacingY} />

      <UseCaseTabs
        isExtended
        title="Accelerate growth with your data"
        description="Personalize experiences, optimize campaigns, and move faster with all of your organization’s data to solve the hardest problems."
      />

      <IndustryLeaderAwards />

      <DestinationMarquee destinations={destinations} my="0" py={sectionSpacingY} />
    </Page>
  );
};

export const getStaticProps = getLandingPageProps("/solutions/marketing", {
  loadDefaultLogos: true,
  loadDefaultMobileLogos: true,
  loadCustomers: [...customersForStats, ...otherFeaturedCustomers],
  loadDestinations: "all",
});

export default Marketing;
